/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Google Cloud Platform", description = "Grant Hightouch access to resources within your Google Cloud project.", lastUpdatedDate = new Date(1731542400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Google Cloud Platform",
        description: "Grant Hightouch access to resources within your Google Cloud project.",
        lastUpdatedDate: "Thu Nov 14 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Managed service account",
          "depth": 1,
          "id": "managed-service-account"
        }, {
          "value": "Bring your own service account",
          "depth": 1,
          "id": "bring-your-own-service-account"
        }, {
          "value": "Google Cloud credential usage",
          "depth": 0,
          "id": "google-cloud-credential-usage"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ul: "ul",
      li: "li",
      a: "a",
      strong: "strong",
      img: "img",
      h3: "h3",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch's Google Cloud integration powers several features:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Workspace deployment on Google Cloud (see ", _jsx(_components.a, {
            href: "/security/regions#supported-regions",
            children: "Regions"
          }), ")"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/security/storage#self-hosted-storage",
            children: "Self-hosted storage via GCS"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/sources/google-bigquery",
            children: "BigQuery"
          }), " source"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/sources/gcs",
            children: "Google Cloud Storage"
          }), " source"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/destinations/gcs",
            children: "Google Cloud Storage"
          }), " destination"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/security/gcp/private-service-connect",
            children: "Google Private Service Connect"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["To give Hightouch access to resources on your GCP account,\ngo to the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/settings/cloud-providers",
          children: _jsx(_components.strong, {
            children: "Cloud providers"
          })
        }), " tab on the Settings page.\nClick ", _jsx(_components.strong, {
          children: "Add cloud provider"
        }), ", and select ", _jsx(_components.strong, {
          children: "Google Cloud Platform"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/cloud-providers/add-new-credential.png",
          alt: "Adding a new cloud provider in Hightouch"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Give your credential a ", _jsx(_components.strong, {
          children: "Display name"
        }), ", then select either ", _jsx(_components.strong, {
          children: "Service account managed by Hightouch"
        }), " or ", _jsx(_components.strong, {
          children: "Bring your own service account"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "managed-service-account",
        children: "Managed service account"
      }), "\n", _jsx(_components.p, {
        children: "When using the managed service account access type, Hightouch creates a service account within our secure Google Cloud account, to which you can bind your project's IAM policies. IAM policies grant permissions within your Google Cloud project."
      }), "\n", _jsxs(_components.p, {
        children: ["When you click ", _jsx(_components.strong, {
          children: "Create a new service account"
        }), ", Hightouch generates a new service account in our project. The service account is unique to your workspace and this credential. You can then copy the service account email and click ", _jsx(_components.strong, {
          children: "Create"
        }), " to confirm the service account creation."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/gcp/managed-1-create.png",
          alt: "Using a managed service acount"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Next, you need to bind IAM roles to this service account by calling ", _jsx(_components.a, {
          href: "https://cloud.google.com/sdk/gcloud/reference/projects/add-iam-policy-binding",
          children: _jsx(_components.code, {
            children: "gcloud projects add-iam-policy-binding"
          })
        }), " via the Google Cloud CLI. The specific roles you grant depend on the types of resources you want to grant Hightouch access to, such as ", _jsx(_components.a, {
          href: "/security/storage",
          children: "Cloud Storage"
        }), " or ", _jsx(_components.a, {
          href: "/sources/google-bigquery",
          children: "BigQuery"
        }), ". Consult the relevant docs for more information on the specific permissions Hightouch needs."]
      }), "\n", _jsx(_components.h3, {
        id: "bring-your-own-service-account",
        children: "Bring your own service account"
      }), "\n", _jsxs(_components.p, {
        children: ["When you ", _jsx(_components.strong, {
          children: "Bring your own service account"
        }), " you upload your service account's key, which Hightouch securely stores."]
      }), "\n", _jsxs(_components.p, {
        children: ["First, you need to ", _jsx(_components.a, {
          href: "https://cloud.google.com/iam/docs/creating-managing-service-accounts",
          children: "create a service account"
        }), ", ", _jsx(_components.a, {
          href: "https://cloud.google.com/iam/docs/granting-changing-revoking-access",
          children: "grant it the relevant roles"
        }), ", and ", _jsx(_components.a, {
          href: "https://cloud.google.com/iam/docs/creating-managing-service-account-keys",
          children: "generate a key"
        }), ". Make sure you create ", _jsx(_components.strong, {
          children: "JSON"
        }), " key, not P12. Once the service account and key have been created, download the key to your machine and drag and drop it into the file selector."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch then parses the key, and if it's valid, you see the ", _jsx(_components.strong, {
          children: "Client ID"
        }), " and ", _jsx(_components.strong, {
          children: "Client Email"
        }), " fields populated. If those correspond with your intended service account, click ", _jsx(_components.strong, {
          children: "Create"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/gcp/byo-1-create.png",
          alt: "Bring your own service acount"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Make sure the service account you attached has the specific roles you need.\nThese permissions depend on the types of resources you want to grant Hightouch access to, such as ", _jsx(_components.a, {
          href: "/security/storage",
          children: "Cloud Storage"
        }), " or ", _jsx(_components.a, {
          href: "/sources/google-bigquery",
          children: "BigQuery"
        }), ".\nConsult the relevant docs for more information on the specific permissions Hightouch needs."]
      }), "\n", _jsx(_components.h2, {
        id: "google-cloud-credential-usage",
        children: "Google Cloud credential usage"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Google Cloud credentials in Hightouch, you can now use them throughout the app, for example for ", _jsx(_components.a, {
          href: "/security/storage",
          children: "external storage"
        }), ", the ", _jsx(_components.a, {
          href: "/sources/google-bigquery",
          children: "BigQuery"
        }), " and ", _jsx(_components.a, {
          href: "/sources/gcs",
          children: "GCS"
        }), " sources, and other Google Cloud integrations like ", _jsx(_components.a, {
          href: "/destinations/gam360",
          children: "Google Ad Manager"
        }), "."]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Regardless of whether you use a managed service account or being your own, ensure it has required permissions.\nConsult the relevant docs for more information on the specific permissions Hightouch needs."
        })
      })]
    });
  }
}
export default MDXContent;
